
import { defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'
import { SignerT, DocumentTypeT, ContractRegister } from '@/core/types/contracts.types'

// validation
import useVuelidate from '@vuelidate/core'
import { required, helpers, email } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useRouter } from 'vue-router'
import useHelpers from '@/common/composable/useHelpers'

// constants
import { D24_TYPES } from '@/core/constants/contracts.constants'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-select': TheSelect,
        'the-input': TheInput,
        'file-reader': FileReader,
    },
    props: {
        contract: {
            type: Object as PropType<ContractRegister>,
            default: null,
        },
        isRegister: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { findObjectByValue } = useHelpers()
        const router = useRouter()

        const state = reactive({
            is_loading: false,
            //
            companyId: null as number | null,
            typeId: null as number | null,
            subdivisionId: null as number | null,
            document: [] as File[],
            signers: [
                { email: null as string | null, dogovor24Type: null as number | null },
                { email: null as string | null, dogovor24Type: null as number | null },
            ] as SignerT[],
            //
            subdivisionName: null as string | null,
            subdivisionShortName: null as string | null,
            subdivisionEmail: null as string | null,
            bin: null as string | null,
            managerEmail: null as string | null,
            number: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                companyId: { required },
                typeId: { required },
                subdivisionId: { required },
                signers: {
                    // @ts-ignore
                    $each: helpers.forEach({
                        email: { required, email },
                        dogovor24Type: { required },
                    }),
                },
                number: { required },
            },
            state
        )

        const data_state = reactive({
            subdivisions: new DataState<any>({}),
            companies: new DataState<CompanyT>({}),
            types: new DataState<DocumentTypeT>({}),
        })

        //

        const loadSubdivisions = async () => {
            try {
                data_state.subdivisions.is_loading = true
                const response = await companies.getSubdivisions({ page: 1 })
                data_state.subdivisions.list = response.data

                data_state.subdivisions.is_failed = false
                data_state.subdivisions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.subdivisions.failure.catchError(error)

                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_failed = true
                }
            } finally {
                if (!data_state.subdivisions.failure.error_state.is_cancel) {
                    data_state.subdivisions.is_loading = false
                }
            }
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page: 1,
                    getAll: 1,
                    limit: 9999,
                    isExtended: 0,
                    search,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 1 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadTypes = async (page: number) => {
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page })
                data_state.types.list = response.data
                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadMoreTypes = async (page: number) => {
            if (page > data_state.types.pagination.total_pages) return
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page })
                data_state.types.list = data_state.types.list.concat(response.data)

                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        //

        const addSigner = () => {
            state.signers.push({
                email: null,
                dogovor24Type: null,
            })
        }

        const deleteItem = (index: number) => {
            if (state.signers.length > 2) state.signers.splice(index, 1)
            else {
                notifications.notify({
                    title: 'Подписантов должно быть как минимум 2',
                    type: 'danger',
                })
            }
        }

        //

        const emitCancel = () => {
            emit('cancel')
        }

        const getPayload = (): FormData | null => {
            const company = findObjectByValue('id', state.companyId, data_state.companies.list)

            if (
                !state.companyId ||
                !state.subdivisionId ||
                !state.subdivisionName ||
                !state.subdivisionShortName ||
                !state.subdivisionEmail ||
                !state.typeId ||
                !state.managerEmail ||
                !state.signers.length ||
                !company ||
                !state.number
            )
                return null

            const form_data = new FormData()

            form_data.append('companyId', state.companyId.toString())
            form_data.append('companyName', company.name)
            form_data.append('companyShortName', company.short_name)
            form_data.append('contactEmail', company.contact_email)
            form_data.append('bin', company.bin)
            //
            form_data.append('contactName', company.contact_name)
            form_data.append('jurAddress', company.jur_address)
            form_data.append('bank', company.bank)
            form_data.append('bik', company.bik)
            form_data.append('bankAccount', company.bank_account)
            form_data.append('companyTypeId', company.type_id.toString())
            form_data.append('kbe', company.kbe.toString())
            form_data.append('contactPhone', company.contact_phone)

            form_data.append('subdivisionId', state.subdivisionId.toString())
            form_data.append('subdivisionName', state.subdivisionName)
            form_data.append('subdivisionShortName', state.subdivisionShortName)
            form_data.append('subdivisionEmail', state.subdivisionEmail)

            form_data.append('typeId', state.typeId.toString())
            form_data.append('managerEmail', state.managerEmail)
            form_data.append('signers', JSON.stringify(state.signers))
            form_data.append('number', state.number)

            if (state.document.length) {
                form_data.append('document', state.document[0])
            }
            if (props.isRegister && props.contract) {
                form_data.append('registerId', props.contract.id.toString())
            }

            return form_data
        }

        const navigate = () => {
            if (props.isRegister) router.push('/contracts/register')
            else router.push('/contracts/to-be-agreed')
        }

        const sendD24 = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const form_data = getPayload()
                if (!form_data) return
                await contracts.sendD24(form_data as FormData)

                notifications.notify({ title: 'Успешно отправлен в договор24', type: 'success' })
                navigate()
            } catch (error: any) {
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onSubdivisionSelected = (subdivision_id: any) => {
            const subdivision = findObjectByValue(
                'id',
                subdivision_id,
                data_state.subdivisions.list
            )

            if (subdivision) {
                state.subdivisionName = subdivision.name
                state.subdivisionShortName = subdivision.short_name
                state.subdivisionEmail = subdivision.contact_email
                state.managerEmail = subdivision.manager.email
            }
        }

        const setInfo = () => {
            if (!props.contract) return

            state.companyId = props.contract.companyId
            state.subdivisionId = props.contract.subdivisionId
            state.number = props.contract.number
            if (props.contract.type) state.typeId = props.contract.type.id

            if (state.subdivisionId) onSubdivisionSelected(state.subdivisionId)
        }

        //

        onBeforeMount(async () => {
            loadTypes(1)
            loadCompanies(1)
            await loadSubdivisions()
            setInfo()
        })

        //

        return {
            state,
            state_form$,
            data_state,
            //
            loadSubdivisions,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            loadTypes,
            loadMoreTypes,
            //
            addSigner,
            deleteItem,
            onSubdivisionSelected,
            sendD24,
            emitCancel,
            //
            D24_TYPES,
        }
    },
})
